/**
 * Employment-type predicates for service-provider records.
 *
 * `isOnboarded` is the flag that drives the public profile badge: true renders
 * "StringsSG Personal Trainer / Verified", false renders "Freelance Trainer /
 * Unverified" (see components/profile/ProfileHeader getCategoryDisplay).
 *
 * The two predicates are deliberately NOT inverses of each other:
 *
 *   isFreelanceTrainer - strict. Only records that explicitly say false. Used
 *     where the page promises freelance trainers specifically, so a record with
 *     the field missing must not be listed as one.
 *
 *   isNotFreelanceTrainer - permissive. Anything that does not explicitly say
 *     false. Used for the sitemap gate, where some payloads omit the field and
 *     excluding them would silently drop good profiles.
 *
 * NOTE: the lite listing payload (`isForContent: true`) does not include
 * `isOnboarded`. Callers that need to filter on employment type must request
 * full records.
 */

export const isFreelanceTrainer = (sp) => sp?.isOnboarded === false

export const isNotFreelanceTrainer = (sp) => sp?.isOnboarded !== false

/** Trainers StringsSG employs directly. */
export const isEmployedTrainer = (sp) => sp?.isOnboarded === true

/**
 * The one gate for "this profile is worth indexing", shared by the sitemap
 * (lib/sitemap selectProfileRows) and the profile pages' robots tag, so a
 * profile Google may index is exactly a profile the sitemap lists. Profile
 * pages are thin (median ~143 words, mostly the bio); indexing near-empty or
 * unverified ones at scale drags site quality down.
 */
export const PROFILE_MIN_BIO_CHARS = 400

// Category is not checked here: the sitemap already fetches by category and
// its rows may omit the field. Callers holding a single record check it.
export const isIndexableTrainerProfile = (sp, minBio = PROFILE_MIN_BIO_CHARS) =>
    isNotFreelanceTrainer(sp) &&
    (sp?.description || '').trim().length >= minBio
